import { Button } from 'components/atoms';
import styled, { css } from 'styled-components';
import fontService, { FontWeight } from 'services/FontService';

export const ResultsWrapper = styled.div`
  display: flex;
  gap: 54px;
  justify-content: center;
  border-radius: 8px;
  border: 1px solid #e7d5b966;
  padding: 24px;
  margin-bottom: 32px;
`;

export const ButtonsWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  column-gap: 16px;
  justify-content: space-between;
  margin-top: 32px;
`;

export const ConfirmButton = styled(Button)``;

export const ClearButton = styled(Button)``;

export const ActionButtonsRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 12px;
`;

export const ActionButton = styled.button<{
  $variant: 'blue' | 'orange' | 'green';
  $disabled?: boolean;
}>`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 24px;
  gap: 12px;
  width: 266px;
  height: 231px;
  border-radius: 8px;
  cursor: pointer;

  background: ${({ $variant }) =>
    $variant === 'blue'
      ? 'linear-gradient(180deg, rgba(18, 16, 17, 0) 0%, #127bad 110%)'
      : $variant === 'orange'
      ? 'linear-gradient(180deg, rgba(18, 16, 17, 0) 0%, #cc6833 110%)'
      : 'linear-gradient(180deg, rgba(18, 16, 17, 0) 0%, #3c8024 110%)'};

  border: 1px solid
    ${({ $variant }) =>
      $variant === 'blue'
        ? 'rgba(87, 181, 227, 0.4)'
        : $variant === 'orange'
        ? 'rgba(243, 129, 68, 0.4)'
        : 'rgba(67, 172, 29, 0.4)'};

  svg {
    width: 48px;
    height: 48px;
  }

  &:hover {
    background: ${({ $variant }) =>
      $variant === 'blue'
        ? 'linear-gradient(180deg, rgba(18, 16, 17, 0) 0%, #1a9fd4 100%)'
        : $variant === 'orange'
        ? 'linear-gradient(180deg, rgba(18, 16, 17, 0) 0%, #e07a3a 100%)'
        : 'linear-gradient(180deg, rgba(18, 16, 17, 0) 0%, #4da32e 100%)'};
    border-color: ${({ $variant }) =>
      $variant === 'blue'
        ? 'rgba(87, 181, 227, 0.8)'
        : $variant === 'orange'
        ? 'rgba(243, 129, 68, 0.8)'
        : 'rgba(87, 227, 116, 0.8)'};
  }

  ${({ $disabled }) =>
    $disabled &&
    css`
      background: #343434;
      border: 1px solid #434343;
      cursor: not-allowed;

      ${ActionButtonLabel} {
        color: #686868;
      }

      svg {
        path {
          fill: #686868;
        }
      }

      &:hover {
        background: #343434;
        border: 1px solid #434343;
      }
    `}
`;

export const ActionButtonLabel = styled.span`
  ${fontService.getFont({ size: 16, lineHeight: 140, weight: FontWeight.bold })};
  text-align: center;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #fff7eb;
  width: 170px;
`;

export const CardSelectionWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 24px;
`;

export const SelectRow = styled.div`
  display: flex;
`;

export const PickerGrid = styled.table`
  table-layout: fixed;
  width: 100%;
  height: 100%;
  border-spacing: 0;
  border-width: 1px;
  border-style: solid;
  border-radius: 8px;

  ${({ theme: { button } }) => css`
    border-color: ${button.primaryBorderLight};
  `};
`;

export const PickerRow = styled.tr``;

export const PickerCell = styled.td`
  height: 33.33%;
`;

export const PickerItem = styled.button<{
  $active?: boolean;
  $color?: 'red' | 'black';
  $ignoreRoundBorder?: boolean;
}>`
  width: 100%;
  height: 100%;
  padding: 26px 0;
  border: none;
  text-align: center;
  user-select: none;
  cursor: pointer;
  background: unset;
  display: flex;
  align-items: center;
  justify-content: center;
  text-transform: uppercase;

  ${fontService.getFont({ size: 18, lineHeight: 100, weight: FontWeight.bold })};

  ${({ $active, $ignoreRoundBorder, $color, theme: { text, button } }) => css`
    box-shadow: ${$active
      ? `0 0 0 1px ${button.primaryBorderLightHover}`
      : `0 0 0 1px ${button.cancelBorder}`};
    color: ${$color === 'red'
      ? '#E04040'
      : $color === 'black'
      ? text.primaryDefault
      : $active
      ? text.primaryDefault
      : text.secondaryDefault};
    background: ${$active
      ? 'linear-gradient(180deg, rgba(187, 147, 112, 0) 0%, #343434 99.99%, #343434 100%)'
      : 'transparent'};
    border-bottom-right-radius: ${$ignoreRoundBorder && '0px !important'};

    &:hover {
      box-shadow: 0 0 0 1px ${button.primaryBorderLightHover};
      background: linear-gradient(180deg, rgba(52, 52, 52, 0) 0%, #343434 99.99%, #343434 100%);
      color: ${$color === 'red' ? '#E04040' : text.primaryDefault};
    }
  `};

  svg {
    width: 40px;
    height: 40px;
  }

  tr:first-child td:first-child & {
    border-top-left-radius: 7px;
  }
  tr:first-child td:last-child & {
    border-top-right-radius: 7px;
  }
  tr:last-child td:first-child & {
    border-bottom-left-radius: 7px;
  }
  tr:last-child td:last-child & {
    border-bottom-right-radius: 7px;
  }
`;

export const CardGroup = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
`;

export const CardRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
`;

export const SideLabel = styled.div<{ $align: 'left' | 'right' }>`
  position: absolute;
  bottom: 0;
  ${fontService.getFont({ size: 14, lineHeight: 150, weight: FontWeight.bold })};
  ${({ theme: { text } }) => css`
    color: ${text.secondaryDefault};
  `}
  width: 100%;
  text-align: ${({ $align }) => $align};
`;

export const CardSlot = styled.div<{ $active?: boolean; $filled?: boolean; $clickable?: boolean }>`
  box-sizing: border-box;
  width: 94px;
  height: 142px;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: center;
  justify-content: center;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};

  ${({ $active, $filled }) => {
    if ($filled)
      return css`
        background: linear-gradient(180deg, rgba(0, 0, 0, 0) 50%, rgba(0, 0, 0, 0.2) 100%), #ffffff;
        box-shadow: -2px 1px 4px rgba(0, 0, 0, 0.2);
        svg {
          width: 32px;
          height: 32px;
        }
      `;
    return css`
      border: ${$active ? '1px solid #e7d5b9' : '1px solid #686868'};
      background: #434343;
    `;
  }}
`;

export const RotatedCardWrapper = styled.div<{ $side: 'left' | 'right' }>`
  width: 142px;
  height: 94px;
  position: relative;
  top: -24px;

  ${CardSlot} {
    width: 100%;
    height: 100%;
    flex-direction: row;
  }
`;
